import {
  SalesRoute, SalesRouteCandidate, SalesRouteStop,
} from './sales-route.models';
import { DayState, DayStop, stopKey } from './sales-route-session';

/**
 * Translation between the server's day and the session's, plus the one sort the
 * Full List does for itself.
 *
 * Pure functions over plain data, which is why they are here and not on the
 * component: they need no HTTP, no view and no session, so a test can state a
 * shape and assert a shape. Living on a component meant every one of these
 * could only be reached through it.
 */

/**
 * One page of the Full List, in the order the table's headers asked for.
 *
 * Unsorted is the SERVER's order, which is not arbitrary: it is round-robin
 * across reps so a cut-off team list loses every rep's tail evenly. Re-sorting
 * that by default would quietly undo it, so no header is active until one is
 * clicked.
 *
 * The tiebreak is always the Lead id. Two doors in the same stage with no
 * value and no follow-up date are otherwise ordered by whatever the sort happens
 * to do with them, and a list that reshuffles between two identical reads is a
 * list nobody trusts.
 */
export function sortCandidates(
  rows: readonly SalesRouteCandidate[], by: string, desc: boolean,
): readonly SalesRouteCandidate[] {
  if (!by) return rows;
  const sign = desc ? -1 : 1;
  // Nulls last in BOTH directions. A blank is the absence of an answer, not the
  // smallest one, so it does not lead the list merely because the arrow flipped.
  const cmp = <T>(a: T | null, b: T | null, order: (x: T, y: T) => number): number => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return order(a, b) * sign;
  };
  const text = (x: string, y: string) => x.localeCompare(y);
  const num = (x: number, y: number) => x - y;

  return [...rows].sort((a, b) => {
    let by0 = 0;
    switch (by) {
      case 'name': by0 = cmp(a.title, b.title, text); break;
      case 'stage': by0 = cmp(a.boardColumn, b.boardColumn, text); break;
      case 'owner': by0 = cmp(a.ownerName, b.ownerName, text); break;
      case 'address': by0 = cmp(a.address, b.address, text); break;
      case 'value': by0 = cmp(a.value || null, b.value || null, num); break;
      case 'due': by0 = cmp(a.nextActionDue, b.nextActionDue, text); break;
      case 'idle': by0 = cmp(a.daysSinceLastActivity, b.daysSinceLastActivity, num); break;
      default: return a.leadId - b.leadId;
    }
    return by0 !== 0 ? by0 : a.leadId - b.leadId;
  });
}

// ── Mapping between the wire shape and the session's ───────────────────────

/** The server's day as the session's state. */
export function toDayState(route: SalesRoute | null): DayState {
  const stops = (route?.stops ?? []).map((stop, index) => routeStopToDayStop(stop, index));
  return { stops, dropped: [], transfers: [], touched: {},
    endpoints: route ? { startAddress: route.startAddress, endAddress: route.endAddress, startLat: route.startLat, startLng: route.startLng,
      endLat: route.endLat, endLng: route.endLng, returnsToStart: route.returnsToStart } : undefined };
}

function routeStopToDayStop(stop: SalesRouteStop, index: number): DayStop {
  return {
    key: stopKey(stop.objectType, stop.objectId),
    stopId: stop.id,
    objectType: stop.objectType,
    objectId: stop.objectId,
    title: stop.title,
    address: stop.address,
    lat: stop.lat,
    lng: stop.lng,
    status: stop.status,
    outcome: stop.outcome,
    pinned: stop.pinned,
    pinReason: stop.pinReason,
    reasons: stop.reasons ?? [],
    plannedArrival: stop.plannedArrival,
    driveSecondsFromPrevious: stop.driveSecondsFromPrevious,
    driveMetersFromPrevious: stop.driveMetersFromPrevious,
    serviceMinutes: stop.serviceMinutes,
    visitedAt: stop.visitedAt,
    note: stop.note,
    savedIndex: index,
  };
}

/** A candidate the patch owes, as a stop proposed for this day. */
export function candidateToStop(candidate: SalesRouteCandidate): DayStop {
  return {
    key: stopKey('Lead', candidate.leadId),
    stopId: null,
    objectType: 'Lead',
    objectId: candidate.leadId,
    title: candidate.title,
    address: candidate.address,
    lat: candidate.lat,
    lng: candidate.lng,
    status: 'Pending',
    outcome: null,
    pinned: false,
    pinReason: null,
    reasons: candidate.reasons ?? [],
    plannedArrival: null,
    driveSecondsFromPrevious: null,
    driveMetersFromPrevious: null,
    serviceMinutes: null,
    visitedAt: null,
    note: null,
    savedIndex: -1,
  };
}

/** Back to the wire shape, for the hand-off links and the visit dialog. The
 *  sequence is the position on screen, not whatever the planner last wrote. */
export function toRouteStop(stop: DayStop, index: number): SalesRouteStop {
  return {
    id: stop.stopId ?? 0,
    objectType: stop.objectType,
    objectId: stop.objectId,
    title: stop.title,
    address: stop.address,
    seq: index + 1,
    pinned: stop.pinned,
    pinReason: stop.pinReason,
    lat: stop.lat,
    lng: stop.lng,
    plannedArrival: stop.plannedArrival,
    driveSecondsFromPrevious: stop.driveSecondsFromPrevious,
    driveMetersFromPrevious: stop.driveMetersFromPrevious,
    serviceMinutes: stop.serviceMinutes,
    status: stop.status,
    outcome: stop.outcome,
    reasons: [...stop.reasons],
    visitedAt: stop.visitedAt,
    note: stop.note,
  };
}
